import { useEffect, useMemo, useState } from "react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { Button, Group, Input, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { CuentaContable } from "@/lib/types";
import { importarCuentasContables, listarCuentasContablesPaginado } from "../services/catalogos-contables-admin-service";
import { useCuentasAdmin } from "../hooks/use-catalogos-contables-admin";
import { useCrearCuenta, useEditarCuenta, useImportarCuentas } from "../hooks/use-catalogos-contables-mutations";
import { ImportarExcelCatalogo } from "./ImportarExcelCatalogo";
import { ItemCatalogoCrearForm } from "./ItemCatalogoCrearForm";
import { ItemCatalogoTable } from "./ItemCatalogoTable";

const PAGE_SIZE = 15;

const EJEMPLOS_CUENTAS = [
  { codigo: "2219010100", nombre: "Proveedores nacionales (ejemplo · reemplázame)" },
  { codigo: "601-01", nombre: "Servicios profesionales (ejemplo · reemplázame)" },
];

/** Gestor de cuentas contables · búsqueda + paginado SERVER-SIDE (los pickers del wizard no cambian). */
export function SeccionCuentas() {
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const serverParams = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [busquedaDebounced, page]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useCuentasAdmin(serverParams);
  const cuentas = data?.items ?? [];
  const total = data?.total ?? 0;

  const [modoCrear, setModoCrear] = useState(false);
  const crear = useCrearCuenta({ onCreado: () => setModoCrear(false) });
  const editar = useEditarCuenta();
  const importar = useImportarCuentas();

  const obtenerTodas = async (): Promise<CuentaContable[]> => {
    const b = busquedaDebounced.trim();
    const todas: CuentaContable[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarCuentasContablesPaginado({ ...(b ? { busqueda: b } : {}), page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Input placeholder="Buscar por código o nombre…" leftSection={<IconSearch size={14} stroke={1.7} />} value={busqueda} onChange={(e) => setBusqueda(e.currentTarget.value)} wrapperClassName="w-70" />
        <Group gap="xs">
          <ImportarExcelCatalogo importar={importar} previsualizar={(filas) => importarCuentasContables(filas, true)} plantillaArchivo="plantilla-cuentas-contables" plantillaHoja="Cuentas contables" ejemplos={EJEMPLOS_CUENTAS} />
          {!modoCrear ? (
            <Button leftSection={<IconPlus size={14} />} onClick={() => setModoCrear(true)}>
              Nueva cuenta
            </Button>
          ) : null}
        </Group>
      </Group>
      {modoCrear ? (
        <ItemCatalogoCrearForm
          copy={{ titulo: "Nueva Cuenta Contable", ejemploCodigo: "601-01", ejemploNombre: "Servicios profesionales" }}
          loading={crear.isPending}
          onCancelar={() => setModoCrear(false)}
          onCrear={crear.mutate}
        />
      ) : null}
      <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
      <ItemCatalogoTable items={cuentas} nombreExport="cuentas-contables" obtenerFilasExport={obtenerTodas} onGuardar={editar.mutate} isPending={editar.isPending} />
      <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="cuentas" />
    </Stack>
  );
}
